/**
 * Vincent B686 (Eric 2026-02-XX) — Page admin d'édition de la stop-list
 * jargon. Remplace l'édition manuelle en curl du JSON `/app/backend/data/
 * jargon_stopwords.json`.
 *
 * Vincent peut :
 *   - Voir la liste actuelle (296 mots initialement) triée alphabétiquement
 *   - Éditer dans un textarea (un mot par ligne, comments avec # supportés
 *     pour organiser)
 *   - Sauvegarder → PUT /api/jargon/stopwords → hot-reload backend
 *   - Voir version + dernière modif
 *
 * Sécurité : la route est déjà protégée admin-only côté frontend (adminOnly)
 * et côté backend (require_admin).
 */
import { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useLang } from "../../contexts/LanguageContext";
import { API_BASE as API } from "../../lib/apiBase";

export default function StopwordsAdminPage() {
  const { lang } = useLang();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [initialLoaded, setInitialLoaded] = useState(false);

  const { data, isPending, isError } = useQuery({
    queryKey: ["jargon-stopwords"],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/api/jargon/stopwords`, {
        withCredentials: true,
      });
      return data;
    },
    staleTime: 30_000,
  });

  // Charge le draft depuis les données une seule fois (évite d'écraser
  // les édits en cours si un refetch se produit en arrière-plan).
  useEffect(() => {
    if (data && !initialLoaded) {
      setDraft((data.words || []).join("\n"));
      setInitialLoaded(true);
    }
  }, [data, initialLoaded]);

  const parsedWords = useMemo(() => {
    return draft
      .split("\n")
      .map((line) => line.replace(/#.*$/, "").trim().toLowerCase())
      .filter(Boolean);
  }, [draft]);

  const dedupedWords = useMemo(() => Array.from(new Set(parsedWords)), [parsedWords]);

  const originalWords = useMemo(() => data?.words || [], [data]);
  const isDirty = useMemo(() => {
    if (originalWords.length !== dedupedWords.length) return true;
    const orig = new Set(originalWords);
    return dedupedWords.some((w) => !orig.has(w));
  }, [dedupedWords, originalWords]);

  const mutation = useMutation({
    mutationFn: async (words) => {
      const { data } = await axios.put(
        `${API}/api/jargon/stopwords`,
        { words },
        { withCredentials: true }
      );
      return data;
    },
    onSuccess: (resp) => {
      toast.success(
        lang === "fr"
          ? `Enregistré (v${resp.version}, ${resp.total} mots)`
          : `Saved (v${resp.version}, ${resp.total} words)`
      );
      queryClient.invalidateQueries({ queryKey: ["jargon-stopwords"] });
      queryClient.invalidateQueries({ queryKey: ["jargon-stopwords", "history"] });
    },
    onError: (err) => {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
          (err?.response?.data?.detail || err.message)
      );
    },
  });

  // Vincent B687 (Eric 2026-02-08) — Historique des 5 dernières versions.
  const { data: historyData } = useQuery({
    queryKey: ["jargon-stopwords", "history"],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/api/jargon/stopwords/history`, {
        withCredentials: true,
      });
      return data;
    },
    staleTime: 30_000,
  });

  const rollbackMutation = useMutation({
    mutationFn: async (version) => {
      const { data } = await axios.post(
        `${API}/api/jargon/stopwords/rollback/${version}`,
        {},
        { withCredentials: true }
      );
      return data;
    },
    onSuccess: (resp) => {
      toast.success(
        lang === "fr"
          ? `Restauré depuis v${resp.restored_from_version} (nouvelle v${resp.version}, ${resp.total} mots)`
          : `Restored from v${resp.restored_from_version} (new v${resp.version}, ${resp.total} words)`
      );
      // Reset le draft pour matcher la nouvelle version chargée
      setInitialLoaded(false);
      queryClient.invalidateQueries({ queryKey: ["jargon-stopwords"] });
      queryClient.invalidateQueries({ queryKey: ["jargon-stopwords", "history"] });
    },
    onError: (err) => {
      toast.error(
        (lang === "fr" ? "Rollback échoué : " : "Rollback failed: ") +
          (err?.response?.data?.detail || err.message)
      );
    },
  });

  const handleSave = () => {
    if (dedupedWords.length > 2000) {
      toast.error(lang === "fr" ? "Maximum 2000 mots" : "Max 2000 words");
      return;
    }
    mutation.mutate(dedupedWords);
  };

  const handleReset = () => {
    setDraft((data?.words || []).join("\n"));
  };

  if (isPending) {
    return (
      <div className="p-8 text-sm text-zinc-500" data-testid="stopwords-loading">
        {lang === "fr" ? "Chargement…" : "Loading…"}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-8 text-sm text-red-600" data-testid="stopwords-error">
        {lang === "fr" ? "Erreur de chargement" : "Load error"}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="stopwords-admin-page">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900" data-testid="stopwords-title">
          {lang === "fr" ? "Stop-liste du jargon" : "Jargon stop-list"}
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          {lang === "fr"
            ? "Mots qui ne doivent JAMAIS être proposés comme candidats jargon. Un mot par ligne, insensible à la casse. Les lignes vides et les commentaires (# ...) sont ignorés."
            : "Words never proposed as jargon candidates. One word per line, case-insensitive. Blank lines and comments (# ...) are ignored."}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 font-medium"
            data-testid="stopwords-count-badge"
          >
            {dedupedWords.length} {lang === "fr" ? "mots" : "words"}
            {isDirty && (
              <span className="ml-2 text-orange-600" data-testid="stopwords-dirty-indicator">
                • {lang === "fr" ? "modifié" : "modified"}
              </span>
            )}
          </span>
          {data?.version != null && (
            <span data-testid="stopwords-version">
              v{data.version}
              {data.updated_at && ` — ${new Date(data.updated_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}`}
              {data.updated_by && ` (${data.updated_by})`}
            </span>
          )}
        </div>
      </header>

      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="w-full min-h-[400px] font-mono text-sm px-3 py-2 border border-zinc-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
        spellCheck={false}
        data-testid="stopwords-textarea"
        placeholder={lang === "fr"
          ? "un\nmot\npar\nligne\n# commentaire ignoré"
          : "one\nword\nper\nline\n# comment ignored"}
      />

      <div className="mt-4 flex items-center gap-3" data-testid="stopwords-actions">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || mutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001a75] disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="stopwords-save-btn"
        >
          {mutation.isPending
            ? (lang === "fr" ? "Enregistrement…" : "Saving…")
            : (lang === "fr" ? "Enregistrer" : "Save")}
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={!isDirty || mutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-zinc-300 text-zinc-700 text-sm font-medium hover:bg-zinc-50 disabled:opacity-50"
          data-testid="stopwords-reset-btn"
        >
          {lang === "fr" ? "Annuler" : "Reset"}
        </button>
      </div>

      {/* Vincent B687 (Eric 2026-02-08) — Historique des 5 dernières versions. */}
      {historyData?.items && historyData.items.length > 0 && (
        <section className="mt-10 border-t border-zinc-200 pt-6" data-testid="stopwords-history-section">
          <h2 className="text-lg font-semibold text-zinc-900">
            {lang === "fr" ? "Historique (5 dernières versions)" : "History (last 5 versions)"}
          </h2>
          <p className="mt-1 text-xs text-zinc-500">
            {lang === "fr"
              ? "Restaure une version antérieure en un clic. La version courante est automatiquement archivée avant restauration."
              : "Rollback in one click. Current version is auto-archived before restore."}
          </p>
          <ul className="mt-4 divide-y divide-zinc-200" data-testid="stopwords-history-list">
            {historyData.items.map((item) => (
              <li
                key={item.version}
                className="flex items-center justify-between py-3 gap-3"
                data-testid={`stopwords-history-item-v${item.version}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-800">
                    v{item.version} — {item.word_count} {lang === "fr" ? "mots" : "words"}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {item.archived_at
                      ? new Date(item.archived_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")
                      : ""}
                    {item.archived_because_of && ` — archivée à cause de : ${item.archived_because_of}`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // eslint-disable-next-line no-alert
                    if (window.confirm(
                      lang === "fr"
                        ? `Restaurer la version v${item.version} (${item.word_count} mots) ?\n\nLa version actuelle sera archivée automatiquement.`
                        : `Restore version v${item.version} (${item.word_count} words)?\n\nThe current version will be auto-archived.`
                    )) {
                      rollbackMutation.mutate(item.version);
                    }
                  }}
                  disabled={rollbackMutation.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-zinc-300 text-zinc-700 text-xs font-medium hover:bg-zinc-50 disabled:opacity-50"
                  data-testid={`stopwords-rollback-btn-v${item.version}`}
                >
                  {lang === "fr" ? "Restaurer" : "Restore"}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
